import Badge from '@material-ui/core/Badge';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CloseIcon from '@material-ui/icons/CloseTwoTone';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCartTwoTone';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { shoppingCartnumberOfSubjectsSelector } from '../../api/shoppingCart';
import ShoppingCartAside from '../../components/ShoppingCartAside';
import ShoppingListsActionsHolder from '../../components/ShoppingListsActionsHolder';
import MainLayout from '../MainLayout';

export interface CartAsideLayoutProps extends StandardDivProps<CartAsideLayoutClassKey> {
  children: NonNullable<React.ReactNode>;
}

export type CartAsideLayoutClassKey =
  | 'root'
  | 'drawer'
  | 'drawerPaper'
  | 'toolbar'
  | 'fab'
  | 'cartAsideWrapper'
  | 'border'
  | 'badge';

const drawerWidth = 290;

const themeSettings = { name: 'CartAsideLayout' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CartAsideLayoutClassKey, {}>({
      root: {},
      drawer: {
        width: drawerWidth,
        flexShrink: 0,
      },
      drawerPaper: {
        width: drawerWidth,
        border: 'none',
        boxShadow: 'none',
        padding: theme.spacing(0, 0),
        overflowY: 'unset',
        flexGrow: 1,
      },
      // necessary for content to be below app bar
      toolbar: {
        ...theme.mixins.toolbar,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      fab: {
        position: 'fixed',
        bottom: theme.spacing(2),
        right: theme.spacing(2),
      },
      cartAsideWrapper: {
        flexGrow: 1,
        display: 'flex',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1, 1),
        margin: theme.spacing(1, 0),
        overflowY: 'auto',
      },
      border: {
        borderStyle: 'solid',
        borderWidth: 2,
        borderColor: theme.palette.primary.light,
      },
      badge: {
        left: 8,
        top: 8,
      },
    }),
  themeSettings,
);

const CartAsideLayout = React.forwardRef<HTMLDivElement, CartAsideLayoutProps>(
  ({ className, classes: pClasses, children }, ref) => {
    const classes = useStyles({ classes: pClasses });

    return (
      <MainLayout ref={ref} className={clsx(classes.root, className)} aside={<CartAsideDrawer classes={classes} />}>
        {children}
        <ShoppingListsActionsHolder />
      </MainLayout>
    );
  },
);

CartAsideLayout.displayName = 'CartAsideLayout';

export default withThemeProps(themeSettings)(CartAsideLayout);

const CartAsideDrawer: React.FC<{ classes: Record<CartAsideLayoutClassKey, string> }> = ({ classes }) => {
  const theme = useTheme();
  const isMobileQry = useMediaQuery(theme.breakpoints.down('lg'));
  const [open, toggleOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(true); // by default hidden

  const subjectsNumber = useRecoilValue(shoppingCartnumberOfSubjectsSelector);

  React.useEffect(() => {
    setIsMobile(isMobileQry);
  }, [isMobileQry]);

  return (
    <>
      <Drawer
        className={classes.drawer}
        variant={isMobile ? 'temporary' : 'permanent'}
        classes={{
          paper: clsx(classes.drawerPaper, { [classes.border]: isMobile }),
        }}
        anchor='right'
        open={open}
      >
        <div className={classes.toolbar}>
          <IconButton onClick={() => toggleOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <Divider />
        <ShoppingCartAside className={clsx(classes.cartAsideWrapper, { [classes.border]: !isMobile })} />
      </Drawer>
      {isMobile && !open && (
        <div className={classes.fab}>
          <Badge
            classes={{
              badge: classes.badge,
            }}
            badgeContent={subjectsNumber || undefined}
            color='secondary'
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Fab color='primary' onClick={() => toggleOpen(true)}>
              <ShoppingCartIcon style={{ color: '#F7D417' }} />
            </Fab>
          </Badge>
        </div>
      )}
    </>
  );
};
